import { useQuery } from '@apollo/client';
import { ChevronLeft } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import {
  MY_CLINICAL_VISIT_QUERY,
  MY_PETS_QUERY,
  MY_PET_CLINICAL_HISTORY_QUERY,
  MY_PET_HOSPITALIZATIONS_QUERY,
} from './portal.gql';

/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment */

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString();
}

function VisitCard({ summary }: { summary: any }): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { data } = useQuery(MY_CLINICAL_VISIT_QUERY, {
    variables: { visit_id: summary.id },
    skip: !open,
    fetchPolicy: 'cache-first',
  });
  const detail = data?.myClinicalVisit;

  return (
    <div className="bg-card rounded-lg border p-4">
      <button type="button" className="w-full text-left" onClick={() => setOpen((v) => !v)}>
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-medium tabular-nums">{fmtDate(summary.date)}</span>
          <span className="text-muted-foreground text-xs">
            {t(`portal.visit_kind_${summary.visit_kind}`, { defaultValue: summary.visit_kind })} ·{' '}
            {summary.diagnosis_count} {t('portal.diagnoses')}
          </span>
        </div>
      </button>
      {open && detail ? (
        <div className="mt-2 space-y-2 border-t pt-2 text-sm">
          {detail.veterinarian_name ? (
            <p className="text-muted-foreground text-xs">
              {t('portal.attended_by')}: {detail.veterinarian_name}
            </p>
          ) : null}
          {detail.diagnoses.length > 0 ? (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide">{t('portal.diagnoses')}</p>
              <ul className="list-disc pl-4">
                {detail.diagnoses.map((d: any, i: number) => (
                  <li key={i}>
                    {d.is_primary ? '★ ' : ''}
                    {d.description}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {detail.prescriptions.some((p: any) => p.lines.length > 0) ? (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide">{t('portal.prescriptions')}</p>
              {detail.prescriptions.flatMap((p: any) =>
                p.lines.map((l: any, i: number) => (
                  <p key={`${p.id}-${i}`} className="text-sm">
                    {l.is_controlled ? '▲ ' : ''}
                    <strong>{l.drug_name}</strong>
                    {l.dose ? ` · ${l.dose}${l.dose_unit ? ' ' + l.dose_unit : ''}` : ''}
                    {l.frequency ? ` · ${l.frequency}` : ''}
                    {l.duration_days ? ` · ${l.duration_days}d` : ''}
                    {l.instructions ? <span className="text-muted-foreground"> — {l.instructions}</span> : null}
                  </p>
                )),
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export function PortalPetDetailPage(): JSX.Element {
  const { t } = useTranslation();
  const { pet_id = '' } = useParams();
  const { data: petsData } = useQuery(MY_PETS_QUERY, { fetchPolicy: 'cache-first' });
  const pet = (petsData?.myPets ?? []).find((p: any) => p.id === pet_id) ?? null;
  const { data: histData } = useQuery(MY_PET_CLINICAL_HISTORY_QUERY, {
    variables: { pet_id },
    skip: !pet_id,
    fetchPolicy: 'cache-and-network',
  });
  const { data: hospData } = useQuery(MY_PET_HOSPITALIZATIONS_QUERY, {
    variables: { pet_id },
    skip: !pet_id,
    fetchPolicy: 'cache-and-network',
  });
  const history: any[] = histData?.myPetClinicalHistory ?? [];
  const hosps: any[] = hospData?.myPetHospitalizations ?? [];

  return (
    <div className="space-y-3">
      <Link to="/portal/pets" className="text-muted-foreground flex items-center gap-1 text-sm">
        <ChevronLeft className="h-4 w-4" aria-hidden /> {t('portal.nav_pets')}
      </Link>
      <div className="bg-card rounded-lg border p-4">
        <h2 className="font-display text-lg font-bold tracking-tight">{pet?.name ?? '—'}</h2>
        <p className="text-muted-foreground text-sm">
          {[pet?.species, pet?.breed, pet?.color].filter(Boolean).join(' · ') || '—'}
        </p>
      </div>

      <section className="space-y-2">
        <h3 className="text-muted-foreground text-xs font-semibold uppercase tracking-wide">
          {t('portal.clinical_history')}
        </h3>
        {history.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t('portal.no_history')}</p>
        ) : (
          history.map((v) => <VisitCard key={v.id} summary={v} />)
        )}
      </section>

      {hosps.length > 0 ? (
        <section className="space-y-2">
          <h3 className="text-muted-foreground text-xs font-semibold uppercase tracking-wide">
            {t('portal.hospitalizations')}
          </h3>
          {hosps.map((h) => (
            <div key={h.id} className="bg-card rounded-lg border p-4 text-sm">
              <div className="flex items-center justify-between">
                <span className="font-medium tabular-nums">{fmtDate(h.admitted_at)}</span>
                <span className="text-muted-foreground text-xs">
                  {t(`portal.hosp_status_${h.status}`, { defaultValue: h.status })}
                </span>
              </div>
              {h.admission_reason ? <p className="text-muted-foreground">{h.admission_reason}</p> : null}
            </div>
          ))}
        </section>
      ) : null}
    </div>
  );
}
